import React, { useState, useEffect } from 'react';
import { useNexusRefresh } from '../context/NexusSyncContext';
import { ImagePlus, MapPin, Plus, Edit2, Trash2, Check, X } from 'lucide-react';
import { useAuth } from './Auth';

const apiBaseUrl = process.env.NODE_ENV === 'development' 
  ? `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}`
  : `${process.env.REACT_APP_API_URL}/api` || 'http://localhost:8000';

const emptyContactForm = {
  first_name: '',
  last_name: '',
  email: '',
  job_title: '',
  comment: '',
};

const normalizeContactPayload = (payload) => {
  const normalized = { ...payload };
  if ('email' in normalized) {
    const trimmedEmail =
      typeof normalized.email === 'string' ? normalized.email.trim() : normalized.email;
    normalized.email = trimmedEmail || null;
  }
  return normalized;
};

const formatAddressLabel = (address) => {
  const parts = [
    address?.recipient,
    address?.address,
    address?.ZIP && address?.city ? `${address.ZIP} ${address.city}` : address?.city,
    address?.country,
  ].filter(Boolean);
  return parts.join(' · ') || 'Adresse';
};

const clientLogoUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;
};

const CLIENT_TYPE_OPTIONS = [
  { value: 'normal', label: 'Client normal' },
  { value: 'investissement_interne', label: 'Investissement interne' },
];

const getClientTypeLabel = (value) =>
  CLIENT_TYPE_OPTIONS.find((option) => option.value === value)?.label || 'Client normal';

const AddressSelect = ({ addresses = [], selectedId, onChange }) => (
  <select
    value={selectedId != null ? selectedId : ''}
    onChange={(event) => {
      const value = event.target.value;
      onChange?.(value ? Number(value) : null);
    }}
    className="border rounded p-2 w-full"
  >
    <option value="">Aucune adresse</option>
    {Array.isArray(addresses) &&
      addresses.map((address) => (
        <option key={address.id} value={address.id}>
          {formatAddressLabel(address)}
        </option>
      ))}
  </select>
);

const ClientContactsSection = ({
  client,
  onCreateContact,
  onUpdateContact,
  onDeleteContact,
}) => {
  const [newContact, setNewContact] = useState(emptyContactForm);
  const [editingContactId, setEditingContactId] = useState(null);
  const [editingValues, setEditingValues] = useState(emptyContactForm);
  const [isAddingContact, setIsAddingContact] = useState(false);

  useEffect(() => {
    setNewContact(emptyContactForm);
    setEditingContactId(null);
    setEditingValues(emptyContactForm);
    setIsAddingContact(false);
  }, [client?.id]);

  const handleNewContactChange = (e) => {
    const { name, value } = e.target;
    setNewContact((prev) => ({ ...prev, [name]: value }));
  };

  const handleNewContactSubmit = async (e) => {
    e.preventDefault();
    if (!newContact.last_name.trim() || !client) return;
    const success = await onCreateContact?.(client.id, newContact);
    if (success) {
      setNewContact(emptyContactForm);
      setIsAddingContact(false);
    }
  };

  const startEditContact = (contact) => {
    setEditingContactId(contact.id);
    setEditingValues({
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      email: contact.email || '',
      job_title: contact.job_title || '',
      comment: contact.comment || '',
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!client || !editingContactId) return;
    const success = await onUpdateContact?.(client.id, editingContactId, editingValues);
    if (success) {
      setEditingContactId(null);
      setEditingValues(emptyContactForm);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!client) return;
    await onDeleteContact?.(client.id, contactId);
    if (editingContactId === contactId) {
      setEditingContactId(null);
      setEditingValues(emptyContactForm);
    }
  };

  if (!client) return null;
  const contacts = Array.isArray(client.contacts) ? client.contacts : [];

  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Contacts clés
          </h4>
          <p className="text-xs text-gray-400">
            {contacts.length === 0 ? 'Aucun contact sauvegardé' : `${contacts.length} contact(s)`}
          </p>
        </div>
        {contacts.length > 0 && (
          <button
            type="button"
            onClick={() => setIsAddingContact((prev) => !prev)}
            className="text-xs font-medium text-blue-600 hover:text-blue-800"
          >
            {isAddingContact ? 'Fermer le formulaire' : 'Ajouter un contact'}
          </button>
        )}
      </div>

      <div className="space-y-3">
        {contacts.map((contact) => {
          const isEditing = editingContactId === contact.id;
          return (
            <div
              key={contact.id}
              className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Sans nom'}
                  </p>
                  <div className="mt-1 space-y-1 text-xs text-gray-600">
                    {contact.email && (
                      <button
                        type="button"
                        onClick={() => navigator.clipboard.writeText(contact.email)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Copier l'email
                      </button>
                    )}
                    {contact.role_in_project && <p>Rôle projet : {contact.role_in_project}</p>}
                    {contact.job_title && <p>Fonction : {contact.job_title}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => startEditContact(contact)}
                    className="rounded-full border border-gray-200 p-1 text-gray-500 hover:text-gray-700"
                    title="Modifier"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteContact(contact.id)}
                    className="rounded-full border border-red-200 p-1 text-red-500 hover:text-red-700"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {contact.comment && (
                <p className="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                  {contact.comment}
                </p>
              )}
              {isEditing && (
                <form onSubmit={handleEditSubmit} className="mt-3 space-y-2 text-sm">
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    <input
                      type="text"
                      name="first_name"
                      value={editingValues.first_name}
                      onChange={handleEditChange}
                      placeholder="Prénom"
                      className="w-full rounded border px-2 py-1"
                    />
                    <input
                      type="text"
                      name="last_name"
                      value={editingValues.last_name}
                      onChange={handleEditChange}
                      placeholder="Nom *"
                      required
                      className="w-full rounded border px-2 py-1"
                    />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={editingValues.email}
                    onChange={handleEditChange}
                    placeholder="Email"
                    className="w-full rounded border px-2 py-1"
                  />
                  <input
                    type="text"
                    name="job_title"
                    value={editingValues.job_title}
                    onChange={handleEditChange}
                    placeholder="Fonction"
                    className="w-full rounded border px-2 py-1"
                  />
                  <textarea
                    name="comment"
                    value={editingValues.comment}
                    onChange={handleEditChange}
                    placeholder="Commentaire"
                    rows={2}
                    className="w-full rounded border px-2 py-1"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingContactId(null)}
                      className="rounded border px-3 py-1 text-gray-600 hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="rounded bg-blue-600 px-3 py-1 text-white hover:bg-blue-700"
                    >
                      Sauvegarder
                    </button>
                  </div>
                </form>
              )}
            </div>
          );
        })}

        {(isAddingContact || contacts.length === 0) && (
          <form
            onSubmit={handleNewContactSubmit}
            className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 space-y-2"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Ajouter un contact du client
              </p>
              {contacts.length > 0 && (
                <button
                  type="button"
                  onClick={() => setIsAddingContact(false)}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Fermer
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <input
                type="text"
                name="first_name"
                value={newContact.first_name}
                onChange={handleNewContactChange}
                placeholder="Prénom"
                className="w-full rounded border px-2 py-1 text-sm"
              />
              <input
                type="text"
                name="last_name"
                value={newContact.last_name}
                onChange={handleNewContactChange}
                placeholder="Nom *"
                required
                className="w-full rounded border px-2 py-1 text-sm"
              />
            </div>
            <input
              type="email"
              name="email"
              value={newContact.email}
              onChange={handleNewContactChange}
              placeholder="Email"
              className="w-full rounded border px-2 py-1 text-sm"
            />
            <input
              type="text"
              name="job_title"
              value={newContact.job_title}
              onChange={handleNewContactChange}
              placeholder="Fonction"
              className="w-full rounded border px-2 py-1 text-sm"
            />
            <textarea
              name="comment"
              value={newContact.comment}
              onChange={handleNewContactChange}
              placeholder="Commentaire"
              rows={2}
              className="w-full rounded border px-2 py-1 text-sm"
            />
            <div className="flex justify-end">
              <button
                type="submit"
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
              >
                Ajouter
              </button>
            </div>
          </form>
        )}

        {!isAddingContact && contacts.length === 0 && (
          <button
            type="button"
            onClick={() => setIsAddingContact(true)}
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            Ajouter un premier contact
          </button>
        )}
      </div>
    </div>
  );
};const ClientManagement = () => {
  const [revision, setRevision] = useState(0);
  useNexusRefresh(['clients'], () => setRevision((value) => value + 1));
  const [clients, setClients] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [uploadingLogoId, setUploadingLogoId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    billing_add_id: null,
    delivery_add_id: null,
    invoice_email: '',
    payment_terms: 'à réception',
    client_type: 'normal',
    notes: ''
  });
  
  const { getAuthHeaders } = useAuth();

  // Charger les données initiales
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Charger les clients
        const clientsResponse = await fetch(`${apiBaseUrl}/clients`, {
          headers: getAuthHeaders()
        });
        
        if (!clientsResponse.ok) {
          const errorData = await clientsResponse.json();
          throw new Error(errorData.detail || 'Échec du chargement des clients');
        }
        
        const clientsData = await clientsResponse.json();
        setClients(Array.isArray(clientsData) ? clientsData : []);

        // Charger les adresses
        const addressesResponse = await fetch(`${apiBaseUrl}/addresses`, {
          headers: getAuthHeaders()
        });
        
        if (!addressesResponse.ok) {
          const errorData = await addressesResponse.json();
          throw new Error(errorData.detail || 'Échec du chargement des adresses');
        }
        
        const addressesData = await addressesResponse.json();
        setAddresses(Array.isArray(addressesData) ? addressesData : []);
      } catch (err) {
        setError(err.message || 'Échec du chargement des données');
        setTimeout(() => setError(null), 3000);
      }
    };

    fetchData();
  }, [getAuthHeaders, revision]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${apiBaseUrl}/clients`, { // Utilisation de apiBaseUrl
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Échec de l\'ajout du client');
      }
      
      const newClient = await response.json();
      setClients(prev => [...prev, newClient]);
      
      // Réinitialiser le formulaire
      setFormData({
        name: '',
        billing_add_id: null,
        delivery_add_id: null,
        invoice_email: '',
        payment_terms: 'à réception',
        client_type: 'normal',
        notes: ''
      });
      
      setSuccess('Client ajouté avec succès');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Échec de l\'ajout du client');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingClient(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddressChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      [type === 'billing' ? 'billing_add_id' : 'delivery_add_id']: value
    }));
  };

  const handleEditAddressChange = (type, value) => {
    setEditingClient(prev => ({
      ...prev,
      [type === 'billing' ? 'billing_add_id' : 'delivery_add_id']: value
    }));
  };

  const handleUpdate = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/clients/${editingClient.id}`, { // Utilisation de apiBaseUrl
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          name: editingClient.name,
          invoice_email: editingClient.invoice_email,
          billing_add_id: editingClient.billing_add_id,
          delivery_add_id: editingClient.delivery_add_id,
          payment_terms: editingClient.payment_terms,
          client_type: editingClient.client_type || 'normal',
          notes: editingClient.notes
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Échec de la mise à jour du client');
      }

      const updatedClient = await response.json();
      setClients(prev =>
        prev.map(client =>
          client.id === updatedClient.id ? updatedClient : client
        )
      );
      setEditingClient(null);
      setSuccess('Client mis à jour avec succès');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Échec de la mise à jour du client');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleLogoUpload = async (clientId, file) => {
    if (!clientId || !file) return;
    setUploadingLogoId(clientId);
    try {
      const body = new FormData();
      body.append('file', file);
      const response = await fetch(`${apiBaseUrl}/clients/${clientId}/logo`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders()
        },
        body,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Échec de l’upload du logo');
      }

      const updatedClient = await response.json();
      setClients(prev =>
        prev.map(client =>
          client.id === updatedClient.id ? updatedClient : client
        )
      );
      setEditingClient(prev => (prev?.id === updatedClient.id ? updatedClient : prev));
      setSuccess('Logo client mis à jour');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Échec de l’upload du logo');
      setTimeout(() => setError(null), 3000);
    } finally {
      setUploadingLogoId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/clients/${id}`, { // Utilisation de apiBaseUrl
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Échec de la suppression du client');
      }

      setClients(prev => prev.filter(client => client.id !== id));
      setSuccess('Client supprimé avec succès');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Échec de la suppression du client');
      setTimeout(() => setError(null), 3000);
    }
  };


  const handleContactCreate = async (clientId, payload) => {
    try {
      const normalizedPayload = normalizeContactPayload(payload);
      const response = await fetch(`${apiBaseUrl}/clients/${clientId}/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(normalizedPayload)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Échec de la création du contact");
      }
      const newContact = await response.json();
      setClients((prev) =>
        prev.map((client) =>
          client.id === clientId
            ? { ...client, contacts: [...(client.contacts || []), newContact] }
            : client
        )
      );
      if (editingClient?.id === clientId) {
        setEditingClient((prev) =>
          prev ? { ...prev, contacts: [...(prev.contacts || []), newContact] } : prev
        );
      }
      setSuccess('Contact ajouté');
      setTimeout(() => setSuccess(null), 2500);
      return true;
    } catch (err) {
      setError(err.message || "Impossible d'ajouter le contact");
      setTimeout(() => setError(null), 3000);
      return false;
    }
  };

  const handleContactUpdate = async (clientId, contactId, payload) => {
    try {
      const normalizedPayload = normalizeContactPayload(payload);
      const response = await fetch(`${apiBaseUrl}/clients/${clientId}/contacts/${contactId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(normalizedPayload)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Échec de la mise à jour du contact");
      }
      const updatedContact = await response.json();
      const replaceList = (list = []) =>
        list.map((contact) => (contact.id === contactId ? updatedContact : contact));
      setClients((prev) =>
        prev.map((client) =>
          client.id === clientId ? { ...client, contacts: replaceList(client.contacts || []) } : client
        )
      );
      if (editingClient?.id === clientId) {
        setEditingClient((prev) =>
          prev ? { ...prev, contacts: replaceList(prev.contacts || []) } : prev
        );
      }
      setSuccess('Contact mis à jour');
      setTimeout(() => setSuccess(null), 2500);
      return true;
    } catch (err) {
      setError(err.message || 'Impossible de mettre à jour le contact');
      setTimeout(() => setError(null), 3000);
      return false;
    }
  };

  const handleContactDelete = async (clientId, contactId) => {
    if (!window.confirm('Supprimer ce contact ?')) return false;
    try {
      const response = await fetch(`${apiBaseUrl}/clients/${clientId}/contacts/${contactId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Échec de la suppression du contact");
      }
      const filterList = (list = []) => list.filter((contact) => contact.id !== contactId);
      setClients((prev) =>
        prev.map((client) =>
          client.id === clientId ? { ...client, contacts: filterList(client.contacts || []) } : client
        )
      );
      if (editingClient?.id === clientId) {
        setEditingClient((prev) =>
          prev ? { ...prev, contacts: filterList(prev.contacts || []) } : prev
        );
      }
      setSuccess('Contact supprimé');
      setTimeout(() => setSuccess(null), 2500);
      return true;
    } catch (err) {
      setError(err.message || 'Impossible de supprimer le contact');
      setTimeout(() => setError(null), 3000);
      return false;
    }
  };

  return (
    <div className="p-2 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-2 flex items-center">
        <MapPin className="h-6 w-6 mr-2" />
        Gestion des Clients
      </h1>

      {/* Affichage des messages d'erreur et de succès */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded">
          {success}
        </div>
      )}

      {/* Formulaire d'ajout de client */}
      <form onSubmit={handleSubmit} className="mb-8 p-4 bg-gray-50 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Nom du client
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="border rounded p-2 w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Email de facturation
            </label>
            <input
              type="email"
              name="invoice_email"
              value={formData.invoice_email}
              onChange={handleInputChange}
              className="border rounded p-2 w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Conditions de paiement
            </label>
            <select
              name="payment_terms"
              value={formData.payment_terms}
              onChange={handleInputChange}
              className="border rounded p-2 w-full"
              required
            >
              <option value="à réception">À réception</option>
              <option value="30 jours nets">30 jours nets</option>
              <option value="45 jours nets">45 jours nets</option>
              <option value="45 jours fin de mois">45 jours fin de mois</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Type de client
            </label>
            <select
              name="client_type"
              value={formData.client_type || 'normal'}
              onChange={handleInputChange}
              className="border rounded p-2 w-full"
            >
              {CLIENT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Adresse de facturation (optionnelle)
            </label>
            <AddressSelect
              selectedId={formData.billing_add_id}
              addresses={addresses}
              onChange={(value) => handleAddressChange('billing', value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Adresse de livraison (optionnelle)
            </label>
            <AddressSelect
              selectedId={formData.delivery_add_id}
              addresses={addresses}
              onChange={(value) => handleAddressChange('delivery', value)}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className="border rounded p-2 w-full"
              rows="3"
            />
          </div>
        </div>

        <button
          type="submit"
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Ajouter un Client
        </button>
      </form>

      {/* Liste des clients */}
      <div className="grid grid-cols-1 gap-4">
        {clients.map((client) => (
          <div 
            key={client.id}
            className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow"
          >
            {editingClient?.id === client.id ? (
              // Mode édition
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Nom du client
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={editingClient.name}
                      onChange={handleEditInputChange}
                      className="border rounded p-2 w-full"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Email de facturation
                    </label>
                    <input
                      type="email"
                      name="invoice_email"
                      value={editingClient.invoice_email}
                      onChange={handleEditInputChange}
                      className="border rounded p-2 w-full"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Conditions de paiement
                    </label>
                    <select
                      name="payment_terms"
                      value={editingClient.payment_terms}
                      onChange={handleEditInputChange}
                      className="border rounded p-2 w-full"
                      required
                    >
                      <option value="à réception">À réception</option>
                      <option value="30 jours nets">30 jours nets</option>
                      <option value="45 jours nets">45 jours nets</option>
                      <option value="45 jours fin de mois">45 jours fin de mois</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Type de client
                    </label>
                    <select
                      name="client_type"
                      value={editingClient.client_type || 'normal'}
                      onChange={handleEditInputChange}
                      className="border rounded p-2 w-full"
                    >
                      {CLIENT_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-3">
                    <label className="block text-sm font-medium text-gray-600 mb-2">
                      Logo client
                    </label>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex h-16 w-28 items-center justify-center rounded bg-white border border-gray-200 overflow-hidden">
                        {editingClient.logo_path ? (
                          <img
                            src={clientLogoUrl(editingClient.logo_path)}
                            alt={`Logo ${editingClient.name}`}
                            className="max-h-full max-w-full object-contain"
                          />
                        ) : (
                          <span className="text-xs text-gray-400">Aucun logo</span>
                        )}
                      </div>
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded border border-blue-200 bg-white px-3 py-2 text-sm font-medium text-blue-700 hover:border-blue-300">
                        <ImagePlus className="h-4 w-4" />
                        {uploadingLogoId === editingClient.id ? 'Upload...' : 'Ajouter un logo'}
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          disabled={uploadingLogoId === editingClient.id}
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            handleLogoUpload(editingClient.id, file);
                            event.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Adresse de facturation
                    </label>
                    <AddressSelect
                      selectedId={editingClient.billing_add_id}
                      addresses={addresses}
                      onChange={(value) => handleEditAddressChange('billing', value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Adresse de livraison
                    </label>
                    <AddressSelect
                      selectedId={editingClient.delivery_add_id}
                      addresses={addresses}
                      onChange={(value) => handleEditAddressChange('delivery', value)}
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={editingClient.notes}
                    onChange={handleEditInputChange}
                    className="border rounded p-2 w-full"
                    rows="3"
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={handleUpdate}
                    className="text-green-500 hover:text-green-700"
                    title="Enregistrer les modifications"
                  >
                    <Check className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingClient(null)}
                    className="text-gray-500 hover:text-gray-700"
                    title="Annuler"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ) : (
              // Mode affichage
              <div>
                <div className="flex justify-between gap-4 mb-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded bg-gray-50 border border-gray-200 overflow-hidden">
                      {client.logo_path ? (
                        <img
                          src={clientLogoUrl(client.logo_path)}
                          alt={`Logo ${client.name}`}
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <span className="text-[10px] text-gray-400">Logo</span>
                      )}
                    </div>
                    <div className="min-w-0">
                    <h3 className="text-lg font-medium">{client.name}</h3>
                    <p className="text-gray-600">{client.invoice_email}</p>
                    <p className="text-sm text-gray-500">
                      Conditions de paiement : {client.payment_terms}
                    </p>
                    <span className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      client.client_type === 'investissement_interne'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {getClientTypeLabel(client.client_type)}
                    </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingClient(client)}
                      className="text-blue-500 hover:text-blue-700"
                      title="Modifier le client"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(client.id)}
                      className="text-red-500 hover:text-red-700"
                      title="Supprimer le client"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {client.notes && (
                  <div className="mt-4 border-t pt-4">
                    <h4 className="font-medium text-gray-700 mb-2">Notes</h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{client.notes}</p>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Adresse de facturation */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                      <MapPin className="h-4 w-4 mr-2" />
                      Adresse de facturation
                    </h4>
                    <div className="text-sm text-gray-600">
                      {client.billing_address ? (
                        <>
                          <p className="font-medium">{client.billing_address.recipient}</p>
                          <p>{client.billing_address.address}</p>
                          <p>{client.billing_address.ZIP} {client.billing_address.city}</p>
                          <p>{client.billing_address.country}</p>
                        </>
                      ) : (
                        <p className="italic text-gray-500">Aucune adresse définie</p>
                      )}
                    </div>
                  </div>

                  {/* Adresse de livraison */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                      <MapPin className="h-4 w-4 mr-2" />
                      Adresse de livraison
                    </h4>
                    <div className="text-sm text-gray-600">
                      {client.delivery_address ? (
                        <>
                          <p className="font-medium">{client.delivery_address.recipient}</p>
                          <p>{client.delivery_address.address}</p>
                          <p>{client.delivery_address.ZIP} {client.delivery_address.city}</p>
                          <p>{client.delivery_address.country}</p>
                        </>
                      ) : (
                        <p className="italic text-gray-500">Aucune adresse définie</p>
                      )}
                    </div>
                  </div>
                </div>

                <ClientContactsSection
                  client={client}
                  onCreateContact={handleContactCreate}
                  onUpdateContact={handleContactUpdate}
                  onDeleteContact={handleContactDelete}
                />
              </div>
            )}
          </div>
        ))}


        {clients.length === 0 && (
          <div className="text-center text-gray-500 p-8 bg-white rounded-lg border">
            Aucun client trouvé
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientManagement;
